'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Children, type JSX, type PropsWithChildren, type ReactElement } from 'react';
import { InternalLink } from '@/components/common/InternalLink';
import { TableVariantContext } from '../Table/Table';
export const Table = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      margin: "-10px 6px 6px 6px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <TableVariantContext.Provider value="large"><table className="w-[calc(100%-12px)] border-separate border-spacing-x-0 border-spacing-y-[10px] tabular-nums" style={t1}>{children}</table></TableVariantContext.Provider>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const Thead = t0 => {
  const $ = _c(12);
  let children;
  let className;
  let position;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      position,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = position;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    position = $[3];
    props = $[4];
  }
  const t1 = position === "sticky" && "sticky top-[calc(64px+10px)]";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("z-1", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== props || $[10] !== t2) {
    t3 = <thead className={t2} {...props}>{children}</thead>;
    $[8] = children;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export const Th = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("text-3xs text-foreground bg-card relative h-[40px] whitespace-nowrap px-md text-start", "[&:first-of-type]:rounded-l-lg [&:last-of-type]:rounded-r-lg", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <th className={t1} {...props}>{children}</th>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const Tbody = t0 => {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <tbody {...props}>{children}</tbody>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export const Tr = t0 => {
  const $ = _c(10);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const isAnchorRow = (Children.toArray(children)[0] as ReactElement<{
    href?: string;
  } | null>).props?.href;
  if (isAnchorRow) {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = cn("relative whitespace-nowrap drop-shadow-md dark:drop-shadow-none", "[&>td>a>div]:bg-card [&:hover>td>a>div]:bg-accent");
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== children || $[5] !== props) {
      t2 = <tr className={t1} {...props}>{children}</tr>;
      $[4] = children;
      $[5] = props;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t1;
  if ($[7] !== children || $[8] !== props) {
    t1 = <tr className="drop-shadow-md dark:drop-shadow-none" {...props}>{children}</tr>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
};
export const Td = t0 => {
  const $ = _c(24);
  let children;
  let className;
  let href;
  let onNavigate;
  let prefetch;
  let props;
  let style;
  let width;
  if ($[0] !== t0) {
    ({
      children,
      href,
      width,
      prefetch,
      onNavigate,
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = href;
    $[4] = onNavigate;
    $[5] = prefetch;
    $[6] = props;
    $[7] = style;
    $[8] = width;
  } else {
    children = $[1];
    className = $[2];
    href = $[3];
    onNavigate = $[4];
    prefetch = $[5];
    props = $[6];
    style = $[7];
    width = $[8];
  }
  let t1;
  if ($[9] !== className) {
    t1 = cn("[&:first-of-type_.td-content]:rounded-l-lg [&:last-of-type_.td-content]:rounded-r-lg", className);
    $[9] = className;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const t2 = width || "auto";
  let t3;
  if ($[11] !== style || $[12] !== t2) {
    t3 = {
      width: t2,
      ...style
    };
    $[11] = style;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== children || $[15] !== href || $[16] !== onNavigate || $[17] !== prefetch) {
    t4 = href ? <InternalLink href={href} prefetch={prefetch} onClick={onNavigate}><div className="td-content bg-card px-md m-0 box-border flex h-[60px] content-center items-center whitespace-nowrap">{children}</div></InternalLink> : <div className="td-content bg-card px-md m-0 box-border flex h-[60px] content-center items-center whitespace-nowrap">{children}</div>;
    $[14] = children;
    $[15] = href;
    $[16] = onNavigate;
    $[17] = prefetch;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== props || $[20] !== t1 || $[21] !== t3 || $[22] !== t4) {
    t5 = <td className={t1} style={t3} {...props}>{t4}</td>;
    $[19] = props;
    $[20] = t1;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
};